import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import { noop } from "lodash-es";
import { useCallback, useState } from "react";

import AiChat from "components/AiChat";
import AiChatHistory from "components/AiChat/History";
import { AiChatSession } from "components/AiChat/types";
import Box from "ds/components/Box";

import AssistantPanelLayoutHeader from "../Layout/Header";
import {
  saveAiChatMessages,
  clearAiChatMessages,
  getInitialMessages,
  getNewChatMessages,
} from "./utils/storage";

const chatTransport = new DefaultChatTransport({
  // eslint-disable-next-line @cspell/spellchecker
  api: "/aiassistant/v1/chat",
  credentials: "include",
});

const AssistantPanelAsk = () => {
  const { messages, sendMessage, setMessages, status, stop } = useChat({
    transport: chatTransport,
    messages: getInitialMessages(),
    onFinish: ({ messages, isAbort }) => {
      if (isAbort) {
        // When aborted during reasoning, the last message may have incomplete reasoning (missing signature).
        // Remove it completely to allow conversation to continue.
        // TODO: This is a temporary fix to allow conversation to continue.
        // We should handle this properly in the backend instead.
        const lastMessage = messages[messages.length - 1];
        const hasIncompleteReasoning = lastMessage?.parts.some(
          (part) => part.type === "reasoning" && !part.providerMetadata?.anthropic?.signature
        );

        if (hasIncompleteReasoning) {
          const cleanedMessages = messages.slice(0, -1);
          setMessages(cleanedMessages);
          saveAiChatMessages(cleanedMessages);
        }
      } else {
        saveAiChatMessages(messages);
      }
    },
    onError: (error) => {
      console.error("AI Chat error:", error);
    },
  });

  const [focusKey, setFocusKey] = useState(0);

  const handleNewChat = useCallback(() => {
    stop();
    clearAiChatMessages();
    setMessages(getNewChatMessages());
    setFocusKey((prev) => prev + 1);
  }, [setMessages, stop]);

  const handleSubmit = useCallback(
    (message: string) => {
      sendMessage({
        role: "user",
        parts: [{ type: "text", text: message }],
      });
    },
    [sendMessage]
  );

  const isStreaming = status === "streaming" || status === "submitted";

  const chats: AiChatSession[] = [
    {
      id: "chat-1",
      name: "Deploy errors in last run",
      updatedAt: "2026-01-28T10:12:00.000Z",
    },
    {
      id: "chat-4",
      name: "Stack drift investigation",
      updatedAt: "2026-01-28T08:05:00.000Z",
    },
    {
      id: "chat-5",
      name: "Terraform plan performance",
      updatedAt: "2026-01-28T06:42:00.000Z",
    },
    {
      id: "chat-2",
      name: "VPC module guidance",
      updatedAt: "2026-01-27T16:45:00.000Z",
    },
    {
      id: "chat-6",
      name: "Kubernetes worker pool sizing",
      updatedAt: "2026-01-27T09:12:00.000Z",
    },
    {
      id: "chat-3",
      name: "Policy review help",
      updatedAt: "2026-01-25T08:20:00.000Z",
    },
    {
      id: "chat-7",
      name: "Module version pinning",
      updatedAt: "2026-01-24T17:30:00.000Z",
    },
    {
      id: "chat-8",
      name: "CI run queue tuning",
      updatedAt: "2026-01-22T14:10:00.000Z",
    },
  ];
  const currentChatId = chats[0]?.id ?? "";
  const loading = false;
  const onSelectChat = noop;

  return (
    <Box direction="column" fullHeight>
      <AssistantPanelLayoutHeader>
        <AiChatHistory
          onNewChat={handleNewChat}
          deleteChat={noop}
          chats={chats}
          currentChatId={currentChatId}
          loading={loading}
          onSelectChat={onSelectChat}
        />
      </AssistantPanelLayoutHeader>
      <AiChat
        messages={messages}
        onSubmit={handleSubmit}
        onStop={stop}
        isStreaming={isStreaming}
        focusKey={focusKey}
      />
    </Box>
  );
};

export default AssistantPanelAsk;
